import stringUtils from '../string';
import { LocalStorageKeys, STORAGE_KEY, STORAGE_VERSION } from './const';
import { CustomObject } from '../../types';

export const storageManager = {
	_getParsedProp<T>(key: string): T | null {
		const namespace = window.localStorage.getItem(`${STORAGE_KEY}${key}`);

		if (namespace === null) {
			return null;
		}

		return JSON.parse(namespace) as T;
	},
	_setParsedProp(key: string, data: unknown) {
		window.localStorage.setItem(`${STORAGE_KEY}${key}`, JSON.stringify(data));
	},
	_removeProp(key: string) {
		window.localStorage.removeItem(`${STORAGE_KEY}${key}`);
	},
	setItem(key: string, data: unknown) {
		if (String(key).length === 0) {
			throw new Error('Provide key for storage manager');
		}

		storageManager._setParsedProp(key, data);
	},
	setSubItem(itemKey: string, subItemKey: string, data: unknown) {
		if (String(itemKey).length === 0 || String(subItemKey).length === 0) {
			throw new Error('Provide itemKey/subItemKey for storage manager');
		}

		let itemNamespace = storageManager.getItem<Record<string, unknown>>(itemKey);

		if (itemNamespace === null || typeof itemNamespace !== 'object') {
			itemNamespace = {};
		}

		itemNamespace[subItemKey] = data;

		storageManager._setParsedProp(itemKey, itemNamespace);
	},
	getItem<T>(key: string): T | null {
		if (String(key).length === 0) {
			throw new Error('Provide key for storage manager');
		}

		const namespace = storageManager._getParsedProp<T>(key);

		if (namespace === null) {
			return null;
		}

		if (typeof namespace === 'object') {
			return namespace;
		}

		return stringUtils.isNullOrEmpty(namespace) ? null : namespace;
	},
	getSubItem<SubItem>(itemKey: string, subItemKey: string): SubItem | null {
		if (String(itemKey).length === 0 || String(subItemKey).length === 0) {
			throw new Error('Provide itemKey/subItemKey for storage manager');
		}

		const namespace = storageManager._getParsedProp<CustomObject>(itemKey);

		if (namespace === null) {
			return null;
		}

		const subItemValue = namespace[subItemKey] as SubItem;

		return typeof subItemValue === 'undefined' ? null : subItemValue;
	},
	removeItem(key: string) {
		if (String(key).length === 0) {
			throw new Error('Provide key for storage manager');
		}

		storageManager._removeProp(key);
	},
	removeSubItem(itemKey: string, subItemKey: string) {
		if (String(itemKey).length === 0) {
			throw new Error('Provide itemKey/subItemKey for storage manager');
		}

		const namespace = storageManager._getParsedProp<CustomObject>(itemKey);

		if (namespace === null) {
			return;
		}

		delete namespace[subItemKey];

		storageManager._setParsedProp(itemKey, namespace);
	},
	cleanUpToVersion() {
		const currentVersion = storageManager.getItem<string>(LocalStorageKeys.StorageVersionKey);

		if (currentVersion === null || currentVersion !== STORAGE_VERSION) {
			window.localStorage.clear();
			storageManager.setItem(LocalStorageKeys.StorageVersionKey, STORAGE_VERSION);
		}
	},
};
